/**
 * Utility to persist and restore optimistic pending subscriptions across page reloads.
 * Uses sessionStorage to maintain pending subscriptions within a browser session.
 */

const STORAGE_KEY = 'daab_optimistic_pending_subscriptions';

/**
 * Save optimistic pending subscriptions to sessionStorage
 * @param {Array} subscriptions - Array of subscription objects
 */
export const persistOptimisticPending = (subscriptions = []) => {
  try {
    const optimisticPending = (Array.isArray(subscriptions)
      ? subscriptions
      : []
    ).filter(
      (sub) =>
        sub?.optimisticApiScopeKey &&
        ((sub?.status || '').toLowerCase() === 'pending' ||
          (sub?.requested_status || '').toLowerCase() === 'subscribe')
    );

    if (optimisticPending.length > 0) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(optimisticPending)
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch (e) {
    console.warn('Failed to persist optimistic pending subscriptions:', e);
  }
};

/**
 * Restore optimistic pending subscriptions from sessionStorage
 * @returns {Array} Array of optimistic pending subscriptions, or empty array if none
 */
export const restoreOptimisticPending = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.warn('Failed to restore optimistic pending subscriptions:', e);
  }
  return [];
};

/**
 * Clear persisted optimistic pending subscriptions
 */
export const clearPersistedOptimisticPending = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to clear persisted optimistic pending:', e);
  }
};

/**
 * Clear optimistic pending for a specific API scope
 * @param {string} apiScopeKey - API scope key in format 'apiId::apiType'
 */
export const clearPersistedOptimisticPendingForScope = (apiScopeKey = '') => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      const subscriptions = JSON.parse(stored);
      const filtered = subscriptions.filter(
        (sub) => sub?.optimisticApiScopeKey !== apiScopeKey
      );

      if (filtered.length > 0) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch (e) {
    console.warn(
      'Failed to clear persisted optimistic pending for scope:',
      e
    );
  }
};
